/**
 * Newsletter signup: feature flag, field options and the CLO-approved copy.
 * Source: AIOpCo Docs & Templates/legal/Newsletter_Legal_Review.md ("Approved Signup Copy"), Witherspoon-CLO 2026-10-10.
 *
 * COPY RULE: the strings in NEWSLETTER_COPY are the approved wording, verbatim. Never edit one in place: change the
 * wording only with a new CLO approval AND a new NEWSLETTER_FORM_VERSION (the server stores the version id with each
 * consent record, so the exact text a person saw can be proven later).
 *
 * FEATURE FLAG: the signup form and everything that links to it stay OFF in every build unless the build sets
 * REACT_APP_NEWSLETTER_SIGNUP_ENABLED=true (this is the "NEWSLETTER_SIGNUP_ENABLED" build-time flag; Create React App only
 * exposes variables with the REACT_APP_ prefix). Do not set it until the CLO conditions are met: Privacy Policy section 6
 * (and 2, 5, 7, 9, 15) published, CMRA mailing address confirmed, double opt-in and consent record live on the server.
 * The token pages (confirm, unsubscribe, preferences) are deliberately NOT behind the flag: an unsubscribe link in a sent
 * email must keep working even if the form is switched off later (CAN-SPAM).
 */

/** Read at call time so tests can switch it; Create React App inlines the value at build time. */
export function newsletterSignupEnabled() {
  return process.env.REACT_APP_NEWSLETTER_SIGNUP_ENABLED === "true";
}

/** Wording version id sent as `form_version`. Bump on ANY change to NEWSLETTER_COPY or the field options. */
// MUST equal a key of newsletter/consent.py CONSENT_TEXTS on the server (an unknown id is refused with 422 UNKNOWN_FORM_VERSION)
// and the copy below must match that text. backend/tests/test_newsletter_integration_contract.py parses this file and fails on drift.
export const NEWSLETTER_FORM_VERSION = "2026-10-v2";

export const NEWSLETTER_PATH = "/newsletter";

/** Values are the API contract; labels are what people read. Order is the CLO order. */
export const NEWSLETTER_FREQUENCIES = [
  { value: "weekly", label: "Weekly", inSentence: "weekly" },
  { value: "biweekly", label: "Every Two Weeks", inSentence: "two-week" }, // not "bi-weekly": that word also reads as twice a week
  { value: "monthly", label: "Monthly", inSentence: "monthly" },
];
/**
 * CMO decision (Newsletter_Cadence_Decision.md): Monthly is preselected in the dropdown. The CLO review says "none
 * preselected"; the Owner ruled (2026-10-11) that only the CONSENT checkbox must start unchecked and the frequency
 * default may be Monthly. Set to "" to require an active choice instead.
 */
export const NEWSLETTER_DEFAULT_FREQUENCY = "monthly";

/** Utility values are the TDU codes in lib/texasTduMap.js (TDU_CODES, same list the bill check uses) plus "not_sure". */
export const NEWSLETTER_UTILITIES = [
  { value: "oncor", label: "Oncor" },
  { value: "centerpoint", label: "CenterPoint Energy Houston Electric" },
  { value: "aep_texas_central", label: "AEP Texas Central" },
  { value: "aep_texas_north", label: "AEP Texas North" },
  { value: "tnmp", label: "Texas-New Mexico Power" },
  { value: "lubbock_power_light", label: "Lubbock Power & Light" },
  { value: "not_sure", label: "Not Sure" },
];

export const PAUSE_DAY_OPTIONS = [30, 60, 90];

export const NEWSLETTER_COPY = {
  heading: "Texas Electricity Price Updates",
  intro:
    "Short updates on average Texas electricity prices, with a few linked news stories on what moved them. Pick how often. We will also send one note if we open plan comparison.",
  emailLabel: "Email",
  utilityLabel: "Your utility",
  frequencyLabel: "How often",
  zipLabel: "ZIP code",
  zipHint: "Used only to confirm your utility area",
  button: "Subscribe",
  // The paragraph under the checkboxes (CLO Newsletter_Consent_v2.md). The Privacy Policy words are rendered as a link, no period.
  noticeBeforeLink:
    "Your email is used for what you tick above. Any other use, we ask first. Unsubscribe anytime, or turn off offers and keep updates.",
  privacyLinkText: "Privacy Policy",
  // CLO 2026-10-13 (Sell_Share_Wording_Ruling.md): no "what we are not" sentence on the form. The site footer carries the required
  // non-affiliation text. "not offers or quotes" is gone too: it contradicts the optional offers box.
  footerLine: "Figures shown are published averages.",
  // SINGLE OPT-IN success state (Owner decision 2026-10-14). The answer is the SAME for a new, an already-subscribed and a suppressed
  // address (no enumeration), so it must be true for all three: no address echoed, no "subscribed" claim, no "we sent". Nothing here asks
  // the visitor to confirm anything.
  successHeading: "You're on the list.",
  successBody: "Check your inbox for a welcome email. If you don't see it, check spam.",
  // Legacy: only people the old flow left waiting for a confirmation link ever see this.
  confirmedHeading: "You are confirmed.",
  // "[frequency]" is filled in by the page.
  confirmedBefore: "Your first update arrives with the next",
  confirmedAfter:
    "issue. Plan comparison is not open and we have no date. Unsubscribe or change how often in any email.",
};

/**
 * CONSENT CHECKBOXES, version 2026-10-v2. Wording is the CLO ruling (AIOpCo Docs & Templates/legal/Newsletter_Consent_v2.md,
 * section 2), verbatim. The full text of a box is `${lead} ${rest}` (lead is shown in bold). It must equal, byte for byte, the
 * text newsletter/consent.py registers and hashes for this form_version: backend/tests/test_newsletter_integration_contract.py
 * parses this file and fails on any drift. Never edit a string in place: a wording change needs CLO approval AND a new version id.
 *
 *   newsletter  REQUIRED. Never pre-ticked. Without it nothing is submitted.
 *   offers      OPTIONAL. Never pre-ticked. Never gates the newsletter.
 *   share       HELD (partner sharing): deliberately NOT defined here, not rendered, and the server refuses it. It returns only on the
 *               CLO's written word (signed partner + 16 TAC 25.486(j) authorization cleared).
 */
export const NEWSLETTER_CONSENTS = {
  newsletter: {
    required: true,
    lead: "Required. Email me the Texas Electricity Price Updates.",
    rest: "I ask RateCurrent LLC to use my email, utility, and ZIP to send these updates at the frequency selected, plus one note if plan comparison opens.",
  },
  offers: {
    required: false,
    lead: "Optional. Also send me offers.",
    rest: "RateCurrent LLC may email me offers from energy providers and home-service companies, and may be paid by them.",
  },
};
export const consentText = (purpose) => `${NEWSLETTER_CONSENTS[purpose].lead} ${NEWSLETTER_CONSENTS[purpose].rest}`;

/** Preferences page: the per-purpose switch for offers. The checkbox text is NEWSLETTER_CONSENTS.offers verbatim. */
export const OFFERS_PREFS = {
  heading: "Offers",
  on: "Saved. You will get offers from energy providers and home-service companies, and still get the updates.",
  off: "Saved. We will not send you offers. You still get the updates.",
  hint: "Turning this off keeps your updates. To stop everything, use Unsubscribe below.",
};

/** /newsletter/offers-off?token= : the footer link in the welcome email. Opening the page changes nothing; the one button turns offers off. */
export const OFFERS_OFF = {
  title: "Turn Off Offers",
  heading: "Turn off offers?",
  intro: "You ticked the offers box when you signed up. Select the button to stop offers. You keep the updates.",
  button: "Turn Off Offers",
  successHeading: "Offers are off",
  success: "We will not send you offers. You still get the updates. You can turn offers back on from your preferences page.",
};

export const SUPPORT_EMAIL = "support@ratecurrent.com";
